import type { CSSProperties } from 'react';
import type { FinishId } from '../../../engine';
import type { LotKind, LotSize, Rarity } from '../../../campaign';
import { Emblem } from './Emblem';

/**
 * The Long Night's things as objects (docs/LONG_NIGHT_ART_DIRECTION.md §7, §12.1–§12.7), each made
 * of the material that says what it is: Superstitions are framed curios of smoked glass in their
 * rarity's metal; Curios sit in round pockets; Appraisals are paper slips with a brass mark; Lots
 * are parcels in paper and string under a wax seal; Fixtures stand under a glass dome; Bargains
 * are black cards of smoke sealed in oxblood; Favors are notes in his hand, folded once.
 */

export type ObjectSpec =
  | {
      readonly kind: 'superstition';
      readonly emblem: string;
      readonly rarity?: Rarity;
      readonly finish?: FinishId;
      /** Stickers (§12.1): On Loan, Bolted, Rotting (0–1, how far the bloom has crept). */
      readonly loan?: boolean;
      readonly bolted?: boolean;
      readonly rot?: number;
      /** A growing value, shown on a brass plate ("× 1.75"). */
      readonly plate?: string;
    }
  | { readonly kind: 'curio'; readonly emblem: string }
  | { readonly kind: 'appraisal'; readonly emblem: string }
  | { readonly kind: 'lot'; readonly lot: LotKind; readonly size: LotSize }
  | { readonly kind: 'fixture'; readonly emblem: string }
  | {
      readonly kind: 'bargain';
      readonly emblem: string;
      /** Pawns it costs, drawn as your own pieces (§12.7); or what it takes, in his ink. */
      readonly pawns: number;
      readonly cost?: string;
    }
  | { readonly kind: 'favor'; readonly emblem: string }
  | { readonly kind: 'carving'; readonly emblem: string }
  | { readonly kind: 'pledge'; readonly emblem: string }
  | { readonly kind: 'emblem'; readonly emblem: string };

/** The string's color for each kind of Lot (§12.5): the Lots' tints, brightened for string. */
export const LOT_STRING: Readonly<Record<LotKind, string>> = {
  card: '#5a86e8',
  hearsay: '#a07ad8',
  appraisal: '#d4b070',
  curio: '#6fae9c',
  carver: '#b07a44',
  mourning: '#5ac8a0',
  omen: '#3aa8b8',
  estate: '#b8485e',
};

/** Assay marks punched into a Superstition's frame: notches by rarity, an eye for Legendary. */
function Assay({ rarity }: { rarity: Rarity }) {
  if (rarity === 'legendary')
    return (
      <svg className="sf-assay" viewBox="0 0 20 8" aria-hidden="true">
        <path d="M2 4 C6 0 14 0 18 4 C14 8 6 8 2 4Z" fill="rgba(0,0,0,0.55)" />
        <circle cx="10" cy="4" r="1.6" fill="rgba(255,240,200,0.8)" />
      </svg>
    );
  const n = rarity === 'common' ? 1 : rarity === 'uncommon' ? 2 : 3;
  return (
    <svg className="sf-assay" viewBox="0 0 20 8" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <rect
          key={i}
          x={10 - n * 2.4 + i * 4.8 + 0.6}
          y="1.5"
          width="2.4"
          height="5"
          rx="0.6"
          fill="rgba(0,0,0,0.55)"
        />
      ))}
    </svg>
  );
}

export function NightObject({
  spec,
  size,
  className = '',
}: {
  spec: ObjectSpec;
  /** The object's width in CSS pixels. */
  size: number;
  className?: string;
}) {
  const w = { '--w': `${size}px` } as CSSProperties;
  switch (spec.kind) {
    case 'superstition': {
      const rarity = spec.rarity ?? 'common';
      return (
        <span
          className={`sf rarity-${rarity} ${spec.finish ? `finish-${spec.finish}` : ''} ${className}`}
          style={w}
        >
          <span className="sf-face">
            {rarity === 'legendary' && <span className="sf-void" />}
            <Emblem name={spec.emblem} size={Math.round(size * 0.66)} />
            {spec.finish && <span className="sf-finish" />}
          </span>
          {size >= 40 && <Assay rarity={rarity} />}
          {spec.plate && size >= 56 && <span className="sf-plate">{spec.plate}</span>}
          {spec.bolted && (
            <span className="sf-rivets" aria-hidden="true">
              <i />
              <i />
              <i />
              <i />
            </span>
          )}
          {spec.rot !== undefined && spec.rot > 0 && (
            <span
              className="sf-rot"
              style={{ '--rot': Math.min(1, spec.rot) } as CSSProperties}
              aria-hidden="true"
            />
          )}
          {spec.loan && <span className="sf-tag" aria-hidden="true" />}
        </span>
      );
    }
    case 'curio':
      return (
        <span className={`cp ${className}`} style={w}>
          <Emblem name={spec.emblem} size={Math.round(size * 0.66)} />
        </span>
      );
    case 'appraisal':
      return (
        <span className={`as ${className}`} style={w}>
          <Emblem name={spec.emblem} size={Math.round(size * 0.5)} />
          <span className="as-mark" aria-hidden="true" />
        </span>
      );
    case 'lot':
      return <Parcel lot={spec.lot} size={spec.size} width={size} className={className} />;
    case 'fixture':
      return (
        <span className={`fd ${className}`} style={w}>
          <span className="fd-dome" />
          <Emblem name={spec.emblem} size={Math.round(size * 0.56)} />
          <span className="fd-base" />
        </span>
      );
    case 'bargain':
      return (
        <span className={`bc-wrap ${className}`} style={w}>
          <span className="bc">
            <Emblem name={spec.emblem} size={Math.round(size * 0.62)} />
            <span className="bc-seal" aria-hidden="true" />
          </span>
          {size >= 48 && (spec.pawns > 0 || spec.cost) && (
            <span className="bc-cost">
              {spec.pawns > 0
                ? Array.from({ length: spec.pawns }, (_, i) => <i key={i} className="bc-pawn" />)
                : spec.cost}
            </span>
          )}
        </span>
      );
    case 'favor':
      return (
        <span className={`fn ${className}`} style={w}>
          <Emblem name={spec.emblem} size={Math.round(size * 0.5)} />
        </span>
      );
    case 'carving':
      return (
        <span className={`ct ${className}`} style={w}>
          <Emblem name={spec.emblem} size={Math.round(size * 0.66)} />
        </span>
      );
    case 'pledge':
      return (
        <span className={`po ${className}`} style={w}>
          <Emblem name={spec.emblem} size={Math.round(size * 0.74)} />
        </span>
      );
    case 'emblem':
      return <Emblem name={spec.emblem} size={size} className={className} />;
  }
}

/** A rectangle of pixels, as path data. */
const px = (x: number, y: number, w = 1, h = 1) => `M${x} ${y}h${w}v${h}h${-w}z`;

/**
 * A Lot (§12.5): a parcel in paper, its string in the kind's color, under a wax seal with the
 * kind's emblem; pixel art, 24 pixels wide. A Normal Lot is small and tied once; a Large one
 * longer and tied twice; a Grand one is a dark box with a ribbon and a bow.
 */
export function Parcel({
  lot,
  size,
  width,
  className = '',
}: {
  lot: LotKind;
  size: LotSize;
  width: number;
  className?: string;
}) {
  const string = LOT_STRING[lot];
  const h = size === 'large' ? 15 : size === 'grand' ? 21 : 18;
  const grand = size === 'grand';
  const W = 24;
  const mid = Math.floor(h / 2);
  // Paper (or the box): light, body and shadow.
  const [hi, body, lo] = grand
    ? ['#4e3c66', '#3a2a4e', '#1c1428']
    : ['#f4e6c8', '#e4d0a8', '#a88a5e'];
  const strings = size === 'large' ? [7, 16] : [11];
  return (
    <span
      className={`lp lp-${size} ${className}`}
      style={{ '--w': `${width}px`, '--h': h / W } as CSSProperties}
    >
      <svg viewBox={`0 0 ${W} ${h}`} shapeRendering="crispEdges" aria-hidden="true">
        {/* The outline, its corners notched. */}
        <path
          fill="#140c22"
          d={px(1, 0, W - 2) + px(1, h - 1, W - 2) + px(0, 1, 1, h - 2) + px(W - 1, 1, 1, h - 2)}
        />
        <path fill={body} d={px(1, 1, W - 2, h - 2)} />
        <path fill={hi} d={px(1, 1, W - 2) + px(1, 2, 1, h - 3)} />
        <path fill={lo} d={px(1, h - 2, W - 2) + px(W - 2, 2, 1, h - 3)} />
        {!grand && (
          // The paper's fold, from the corners to the middle.
          <path
            fill={lo}
            opacity="0.55"
            d={Array.from(
              { length: 5 },
              (_, i) => px(2 + i * 2, 3 + i) + px(W - 4 - i * 2, 3 + i),
            ).join('')}
          />
        )}
        <path
          fill={string}
          d={
            strings.map((x) => px(x, 1, grand ? 2 : 1, h - 2)).join('') +
            px(1, mid, W - 2, grand ? 2 : 1)
          }
        />
        {grand && (
          // The bow.
          <path
            fill={string}
            d={
              px(7, mid - 3, 3, 2) +
              px(6, mid - 2, 2, 2) +
              px(14, mid - 3, 3, 2) +
              px(16, mid - 2, 2, 2)
            }
          />
        )}
      </svg>
      <span className="lp-seal">
        <Emblem name={`lot.${lot}`} size={Math.round(width * 0.24)} />
      </span>
    </span>
  );
}
